import React, { useState } from 'react'
import {
  Upload,
  Button,
  Form,
  Input,
  Typography,
  message,
  List,
  Spin,
  Modal,
} from 'antd'
import {
  UploadOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { uploadFile, deleteFile, submitFiles } from '../../api/doc'
import type { UploadFile } from 'antd/es/upload/interface'

const { Title, Text } = Typography

interface FormValues {
  title: string
}

// 定义文件在服务端的返回信息
interface ServerFile {
  uid: string
  name: string
  url?: string
}

const UploadPage: React.FC = () => {
  const [form] = Form.useForm<FormValues>()
  const [fileList, setFileList] = useState<ServerFile[]>([])
  const [imageList, setImageList] = useState<UploadFile[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewTitle, setPreviewTitle] = useState('')
  // 上传文件
  const customUpload = async (options: any) => {
    const { file, onSuccess, onError } = options

    try {
      const res = await uploadFile(file)

      if (res.code == 200) {
        const newFile: ServerFile = {
          uid: file.uid,
          name: file.name,
          url: res.data,
        }
        setFileList((prev) => [...prev, newFile])
        message.success(`${file.name} 上传成功`)
        onSuccess && onSuccess(res.data)
      } else {
        message.error(`${file.name} 上传失败`)
        onError && onError(new Error(res.data.msg || '上传失败'))
      }
    } catch (err: any) {
      message.error(`${file.name} 上传失败: ${err.message}`)
      onError && onError(err)
    }
  }
  const handleImageChange = async ({
    fileList,
  }: {
    fileList: UploadFile[]
  }) => {
    // 只保留一张
    const latest = fileList.slice(-1)

    // 如果有新文件，立即上传
    const file = latest[0]?.originFileObj

    if (file) {
      try {
        const res = await uploadFile(file)
        if (res.code == '200') {
          message.success('图片上传成功')
          // 替换成带 url 的
          setImageList([
            {
              uid: file.uid,
              name: file.name,
              status: 'done',
              url: res.data,
            },
          ])
        }
      } catch (err: any) {
        message.error(`图片上传失败: ${err.message}`)
      }
    } else {
      setImageList(latest)
    }
  }
  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || '')
    setPreviewVisible(true)
    setPreviewTitle(file.name || '图片预览')
  }
  // 删除文件
  const handleRemove = async (file: ServerFile | UploadFile) => {
    if (file.url) {
      const res = await deleteFile(file.url) // 调用删除接口
      if (res.code == 200) message.success(`${file.name} 已删除`)
    } else {
      message.error(`${file.name} 删除失败`)
    }
  }

  // 提交
  const handleSubmit = async () => {
    const values = await form.validateFields()

    if (fileList.length === 0) {
      message.warning('请先上传文件')
      return
    }
    if (imageList.length === 0) {
      message.warning('请先上传图标')
      return
    }
    setLoading(true)
    try {
      const res = await submitFiles({
        ragName: values.ragName,
        ragTag: values.ragTag,
        ragDesc: values.ragDesc,
        ragFileUrls: fileList.map((file) => file.url),
        iconUrl: imageList[0]?.url || null,
      })
      if (res.code == '200') {
        message.success('提交成功')
        form.resetFields()
        setFileList([])
        setImageList([])
      }
    } catch (err: any) {
      message.error(`提交失败: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f3f4f6',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 16,
      }}
    >
      <div
        style={{
          background: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          width: 400,
          position: 'relative',
        }}
      >
        <Spin
          spinning={loading}
          indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />}
          tip="提交中，请稍候..."
        >
          <Title level={4} style={{ textAlign: 'center', marginBottom: 24 }}>
            添加知识
          </Title>

          <Form form={form} layout="vertical">
            <Form.Item
              label="知识标题"
              name="ragName"
              rules={[{ required: true, message: '请输入知识标题' }]}
            >
              <Input placeholder="输入知识标题" />
            </Form.Item>
            <Form.Item
              label="知识标识字符"
              name="ragTag"
              rules={[{ required: true, message: '请输入知识标识字符' }]}
            >
              <Input placeholder="输入知识标识字符" />
            </Form.Item>
            <Form.Item
              label="描述词"
              name="ragDesc"
              rules={[{ required: true, message: '请输入描述词' }]}
            >
              <Input placeholder="输入描述词" />
            </Form.Item>
            <Form.Item label="上传图片">
              <Upload
                listType="picture-card"
                fileList={imageList}
                onChange={handleImageChange}
                beforeUpload={() => false} // 阻止默认上传
                onRemove={(file) => {
                  handleRemove(file)
                  setImageList([])
                }} // 删除时清空
                accept="image/*"
                onPreview={handlePreview}
              >
                {imageList.length >= 1 ? null : (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>上传</div>
                  </div>
                )}
              </Upload>
              <Modal
                open={previewVisible}
                title={previewTitle}
                footer={null}
                onCancel={() => setPreviewVisible(false)}
              >
                <img
                  alt="preview"
                  style={{ width: '100%' }}
                  src={previewImage}
                />
              </Modal>
            </Form.Item>
            <Form.Item label="上传文件">
              <Upload.Dragger
                multiple
                customRequest={customUpload}
                showUploadList={false} // 自定义文件列表
                accept=".pdf,.csv,.txt,.md,.sql,.java"
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p>将文件拖到此处或点击上传</p>
                <Text type="secondary">
                  支持类型：.pdf, .csv, .txt, .md, .sql, .java
                </Text>
              </Upload.Dragger>
            </Form.Item>

            {fileList.length > 0 && (
              <List
                bordered
                dataSource={fileList}
                style={{ marginTop: 12 }}
                renderItem={(file) => (
                  <List.Item
                    actions={[
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={() => {
                          handleRemove(file)
                          setFileList((prev) =>
                            prev.filter((item) => item.uid !== file.uid)
                          )
                        }}
                      />,
                    ]}
                  >
                    {file.name}
                  </List.Item>
                )}
              />
            )}

            <Form.Item style={{ marginTop: 24, textAlign: 'center' }}>
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                提交
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </div>
  )
}

export default UploadPage
